import { InfoIcon, TriangleAlert } from 'lucide-react'
import React, { type ReactNode } from 'react'
import { Card, CardContent } from '#app/components/ui/card'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '#app/components/ui/tooltip'
import { cn } from '#app/utils/misc'
import { Button } from './ui/button'

interface MonitorFeatureListItemProps {
	title: string
	description: string
	icon: ReactNode
	status: 'online' | 'submitting' | 'offline' | 'processing'
}

export function MonitorFeatureListItem({
	title,
	description,
	icon,
	status,
}: MonitorFeatureListItemProps) {
	return (
		<Card
			className={cn(
				'mx-auto max-w-2xl',
				status === 'offline' && 'border-2 border-red-700',
			)}
		>
			<CardContent className="p-2 sm:p-4">
				<div className="flex items-center space-x-4">
					<div className="relative flex h-12 w-6 shrink-0 items-center justify-center">
						{React.cloneElement(icon as React.ReactElement, {
							className: 'h-6 w-6 rounded-full',
						})}
					</div>
					<div className="min-w-0 flex-grow">
						<h3 className="truncate text-lg font-semibold leading-none">
							{title}
							<span className="ml-2 inline-flex items-center">
								<TooltipProvider>
									<Tooltip>
										<TooltipTrigger asChild>
											<Button
												variant="ghost"
												size="icon"
												className="h-4 w-4 p-0"
											>
												<InfoIcon className="h-4 w-4 text-muted-foreground" />
												<span className="sr-only">More info</span>
											</Button>
										</TooltipTrigger>
										<TooltipContent className="w-80 text-sm">
											<p>{description}</p>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							</span>
						</h3>
						<p className="mt-1 line-clamp-2 text-xs">
							{status === 'online' && (
								<span className="font-semibold text-green-600">Ready</span>
							)}
							{status === 'offline' && (
								<span className="font-semibold text-red-600">
									<TriangleAlert className="inline-block w-4" /> Error
								</span>
							)}
							{status === 'processing' || status === 'submitting' ? (
								<span className="animate-pulse font-semibold text-yellow-600">
									{status === 'processing' ? 'Processing' : 'Preparing'}
								</span>
							) : null}
						</p>
					</div>
				</div>
			</CardContent>
		</Card>
	)
}
